import { lookupRegion, REGION_CODES } from '../data/regionCodes';

/** 校验结果 */
export interface IdCardResult {
  raw: string;
  valid: boolean;
  checks: {
    length: boolean;
    digits: boolean;
    checkDigit: boolean;
    birthDate: boolean;
    checksum: boolean;
    region: boolean;
  };
  info?: {
    birthDate: string;   // yyyy-MM-dd
    age: number;
    gender: '男' | '女';
    region: string;
  };
  errors: string[];
}

/** 生成选项 */
export interface GenerateOptions {
  /** 最小年龄（周岁），默认 1 */
  minAge?: number;
  /** 最大年龄（周岁），默认 120 */
  maxAge?: number;
  /** 性别限制，默认 undefined 表示不限 */
  gender?: '男' | '女';
  /** 是否允许末位为 X，默认 true */
  allowX?: boolean;
}

/** 加权因子 ISO 7064:1983 MOD 11-2 */
const WEIGHTS = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
/** 校验码映射 */
const CHECK_CODES = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];

/** 判断是否为闰年 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** 校验出生日期是否合法 */
function isValidDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  if (day < 1) return false;

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
}

/**
 * 校验单个身份证号
 */
export function validateIdCard(input: string): IdCardResult {
  const raw = input.trim();
  const errors: string[] = [];
  const checks: IdCardResult['checks'] = {
    length: false,
    digits: false,
    checkDigit: false,
    birthDate: false,
    checksum: false,
    region: false,
  };

  // ① 长度校验
  checks.length = raw.length === 18;
  if (!checks.length) {
    errors.push('长度不是18位');
  }

  // ② 前17位数字 + 末位数字或X
  checks.digits = /^\d{17}$/.test(raw.slice(0, 17));
  checks.checkDigit = /^[\dXx]$/.test(raw[17] ?? '');
  if (!checks.digits) {
    errors.push('前17位必须为数字');
  }
  if (!checks.checkDigit) {
    errors.push('末位必须为数字或X');
  }

  // 如果格式不通过，后续校验无意义
  if (!checks.length || !checks.digits || !checks.checkDigit) {
    return { raw, valid: false, checks, errors };
  }

  // ③ 出生日期校验
  const year = parseInt(raw.slice(6, 10), 10);
  const month = parseInt(raw.slice(10, 12), 10);
  const day = parseInt(raw.slice(12, 14), 10);
  checks.birthDate = isValidDate(year, month, day);
  if (!checks.birthDate) {
    errors.push('出生日期不合法');
  }

  // ④ 校验码验证
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    sum += parseInt(raw[i], 10) * WEIGHTS[i];
  }
  const expectedCheckCode = CHECK_CODES[sum % 11];
  checks.checksum = raw[17].toUpperCase() === expectedCheckCode;
  if (!checks.checksum) {
    errors.push(`校验码不匹配（期望: ${expectedCheckCode}）`);
  }

  // ⑤ 地区码校验
  const region = lookupRegion(raw.slice(0, 6));
  checks.region = region !== null;
  if (!checks.region) {
    errors.push('地区码无效');
  }

  const valid = Object.values(checks).every(Boolean);

  // 信息提取
  const info = valid
    ? {
        birthDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        age: calculateAge(year, month, day),
        gender: parseInt(raw[16], 10) % 2 === 1 ? ('男' as const) : ('女' as const),
        region: region!,
      }
    : undefined;

  return { raw, valid, checks, info, errors };
}

/** 计算年龄（周岁） */
function calculateAge(birthYear: number, birthMonth: number, birthDay: number): number {
  const today = new Date();
  let age = today.getFullYear() - birthYear;
  const m = today.getMonth() + 1;
  const d = today.getDate();
  if (m < birthMonth || (m === birthMonth && d < birthDay)) {
    age--;
  }
  return age;
}

/**
 * 批量校验
 * 输入多行文本，每行一个号码，空行跳过
 */
export function validateIdCards(input: string): IdCardResult[] {
  return input
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(validateIdCard);
}

/** 收集所有有效的6位地区码作为生成池 */
function getRegionCodePool(): string[] {
  const pool: string[] = [];
  const keys = Object.keys(REGION_CODES);

  for (const key of keys) {
    if (key.length === 6) {
      pool.push(key);
    } else if (key.length === 4) {
      // 地级市编码 + "00" 代表市辖区
      pool.push(key + '00');
    }
  }

  return [...new Set(pool)];
}

/** 从年龄区间随机生成一个合法出生日期 */
function randomBirthDate(
  minAge: number,
  maxAge: number
): { year: number; month: number; day: number } {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  // 最早出生日: today - maxAge 岁
  const earliest = new Date(todayYear - maxAge, todayMonth - 1, todayDay);
  // 最晚出生日: today - minAge 岁
  const latest = new Date(todayYear - minAge, todayMonth - 1, todayDay);

  const rangeMs = latest.getTime() - earliest.getTime();
  const randomMs = Math.floor(Math.random() * (rangeMs + 1));
  const d = new Date(earliest.getTime() + randomMs);

  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}

/**
 * 生成一个合法身份证号
 */
export function generateIdCard(options?: GenerateOptions): string {
  const rawMin = options?.minAge ?? 1;
  const rawMax = options?.maxAge ?? 120;
  // 自动修正：确保 min ≤ max
  const effectiveMinAge = Math.min(rawMin, rawMax);
  const effectiveMaxAge = Math.max(rawMin, rawMax);
  const gender = options?.gender;
  const allowX = options?.allowX ?? true;

  // ① 随机地区码
  const regionPool = getRegionCodePool();
  const regionCode = regionPool[Math.floor(Math.random() * regionPool.length)];

  // ② 随机出生日期
  const { year, month, day } = randomBirthDate(effectiveMinAge, effectiveMaxAge);
  const birthStr =
    String(year).padStart(4, '0') +
    String(month).padStart(2, '0') +
    String(day).padStart(2, '0');

  const body = regionCode + birthStr;

  // ③ 顺序码 + 校验码，循环直到满足 allowX 条件
  let checkCode = '';
  let seqStr = '';
  let attempts = 0;

  do {
    let seq: number;
    if (gender === '男') {
      // 奇数 → 男（第17位为奇数）
      seq = Math.floor(Math.random() * 500) * 2 + 1;
    } else if (gender === '女') {
      // 偶数 → 女（第17位为偶数）
      seq = Math.floor(Math.random() * 500) * 2;
    } else {
      seq = Math.floor(Math.random() * 1000);
    }
    seqStr = String(seq).padStart(3, '0');

    const id17 = body + seqStr;
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      sum += parseInt(id17[i], 10) * WEIGHTS[i];
    }
    checkCode = CHECK_CODES[sum % 11];
    attempts++;
  } while (!allowX && checkCode === 'X' && attempts < 100);

  return body + seqStr + checkCode;
}

/**
 * 批量生成合法身份证号
 * 结果自动去重
 */
export function generateIdCards(count: number, options?: GenerateOptions): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  const maxAttempts = count * 10; // 防止极端条件下死循环
  let attempts = 0;

  while (result.length < count && attempts < maxAttempts) {
    const id = generateIdCard(options);
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
    attempts++;
  }

  return result;
}
